import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const WASTE_CATEGORIES = [
  { value: 'broken', label: 'שבור/נזק', icon: '💥' },
  { value: 'lost', label: 'אבד', icon: '❓' },
  { value: 'expired', label: 'פג תוקף', icon: '⏰' },
  { value: 'damaged', label: 'פגום', icon: '⚠️' },
  { value: 'defective', label: 'פגם יצרני', icon: '🔧' },
  { value: 'other', label: 'אחר', icon: '📝' }
];

const AgentWasteReport = () => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  
  const [formData, setFormData] = useState({
    warehouse_type: 'b2b',
    quantity: 1,
    waste_category: 'broken',
    reason: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchRecentReports();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchProducts();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const searchProducts = async () => {
    try {
      const response = await axios.get('/api/products/quick-search', {
        params: { q: searchTerm, limit: 10 }
      });
      setSearchResults(response.data.results || response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
    }
  };

  const fetchRecentReports = async () => {
    try {
      const response = await axios.get('/api/waste-reports', {
        params: { limit: 5 }
      });
      setRecentReports(response.data.reports || []);
    } catch (error) {
      console.error('Error fetching recent reports:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setSearchTerm('');
    setSearchResults([]);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedProduct) {
      newErrors.product = 'יש לבחור מוצר';
      toast.error('יש לבחור מוצר');
      return false;
    }

    const qty = parseInt(formData.quantity);
    if (!qty || qty < 1) {
      newErrors.quantity = 'כמות חייבת להיות לפחות 1';
    }

    const availableStock = formData.warehouse_type === 'b2c' 
      ? selectedProduct.stock_b2c 
      : selectedProduct.stock_b2b;
    
    if (qty > availableStock) {
      newErrors.quantity = `מלאי זמין: ${availableStock}`;
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'סיבה היא שדה חובה';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'סיבה חייבת להכיל לפחות 10 תווים';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/waste-reports', {
        product_id: selectedProduct.id,
        warehouse_type: formData.warehouse_type,
        quantity: parseInt(formData.quantity),
        waste_category: formData.waste_category,
        reason: formData.reason.trim()
      });

      toast.success(`✅ דוח בלאי נוצר בהצלחה`);
      
      // Reset form
      setSelectedProduct(null);
      setFormData({
        warehouse_type: 'b2b',
        quantity: 1,
        waste_category: 'broken',
        reason: ''
      });
      setErrors({});
      
      // Refresh recent reports
      fetchRecentReports();
    } catch (error) {
      console.error('Error creating waste report:', error);
      toast.error(error.response?.data?.message || 'שגיאה ביצירת דוח בלאי');
    } finally {
      setLoading(false);
    }
  };

  const availableStock = selectedProduct 
    ? (formData.warehouse_type === 'b2c' ? selectedProduct.stock_b2c : selectedProduct.stock_b2b)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">דיווח בלאי</h1>
        <p className="text-gray-600 mt-1">דווח על מוצרים שנשברו, אבדו או ניזוקו</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            {/* Product Search */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                בחר מוצר <span className="text-red-500">*</span>
              </label>
              
              {selectedProduct ? (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900">{selectedProduct.name}</div>
                      <div className="text-sm text-gray-600 mt-1">מק"ט: {selectedProduct.sku}</div>
                      <div className="text-xs text-gray-500 mt-1 flex gap-4">
                        <span>B2C: {selectedProduct.stock_b2c || 0}</span>
                        <span>B2B: {selectedProduct.stock_b2b || 0}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedProduct(null)}
                      className="text-red-600 hover:text-red-700 text-sm font-bold"
                    >
                      שנה
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="חפש מוצר..."
                    className="w-full pr-10 pl-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium"
                  />
                  
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto">
                      {searchResults.map(product => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleProductSelect(product)}
                          className="w-full px-4 py-3 text-right hover:bg-primary-50 transition-colors border-b border-gray-100"
                        >
                          <div className="font-semibold text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-600">מק"ט: {product.sku}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Warehouse Type & Quantity */}
            {selectedProduct && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      סוג מלאי
                    </label>
                    <select
                      name="warehouse_type"
                      value={formData.warehouse_type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-medium"
                    >
                      <option value="b2c">B2C (זמין: {selectedProduct.stock_b2c || 0})</option>
                      <option value="b2b">B2B (זמין: {selectedProduct.stock_b2b || 0})</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      כמות <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="1"
                      max={availableStock}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 font-bold text-lg ${
                        errors.quantity ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.quantity && <p className="mt-1 text-sm text-red-600 font-medium">{errors.quantity}</p>}
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    סיבת בלאי <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {WASTE_CATEGORIES.map(category => (
                      <button
                        key={category.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, waste_category: category.value }))}
                        className={`p-3 border-2 rounded-lg transition-all ${
                          formData.waste_category === category.value
                            ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{category.icon}</span>
                          <span className="font-semibold text-sm">{category.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    הסבר מפורט <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    rows="4"
                    maxLength="200"
                    placeholder="תאר בפירוט מה קרה למוצר (מינימום 10 תווים)..."
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 font-medium ${
                      errors.reason ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  <div className="flex justify-between mt-1">
                    <p className="text-sm text-gray-600 font-medium">
                      {formData.reason.length}/200
                    </p>
                    {errors.reason && <p className="text-sm text-red-600 font-bold">{errors.reason}</p>}
                  </div>
                </div>

                {/* Warning */}
                {formData.quantity > 0 && (
                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                      <div className="text-sm text-yellow-900">
                        <p className="font-bold mb-1">שים לב!</p>
                        <p>
                          הדיווח יוריד <strong className="text-red-600">{formData.quantity} יחידות</strong> ממלאי {formData.warehouse_type === 'b2c' ? 'B2C' : 'B2B'}.
                        </p>
                        <p className="mt-1">
                          מלאי חדש: <strong>{availableStock - formData.quantity}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-bold text-lg shadow-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      <span>שולח דיווח...</span>
                    </>
                  ) : (
                    <>
                      <ExclamationTriangleIcon className="h-6 w-6" />
                      <span>דווח בלאי</span>
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </div>

        {/* Recent Reports Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">דיווחים אחרונים</h2>
            
            {recentReports.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">אין דיווחים אחרונים</p>
            ) : (
              <div className="space-y-3">
                {recentReports.map(report => {
                  const category = WASTE_CATEGORIES.find(c => c.value === report.waste_category);
                  return (
                    <div key={report.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-gray-900 truncate">
                            {report.product?.name}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {category?.icon} {category?.label} • {Math.abs(report.quantity)} יח'
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(report.created_at).toLocaleDateString('he-IL')}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Stats */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg border-2 border-red-200 p-6 mt-4">
            <h3 className="font-bold text-gray-900 mb-3">💡 טיפ</h3>
            <ul className="text-sm text-gray-700 space-y-2">
              <li>✓ וודא שהכמות נכונה</li>
              <li>✓ תאר בפירוט את הסיבה</li>
              <li>✓ בחר את סוג המלאי הנכון</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentWasteReport;

